/**
 * BTA Loading Overlay - Complete Version (Coordinated with bta-boarding-main.js)
 * Version: 2.1 - Site-branded overlay (dark grey card, gold border and spinner)
 *          2.0 - MutationObserver-based calendar detection
 *
 * CHANGES IN 2.0:
 * - Replaced polling-based calendar detection with MutationObserver
 *   Overlay now hides the instant BTA renders date buttons, not after a fixed delay
 * - Replaced polling-based month-change detection with MutationObserver
 * - Added hard 15-second safety timeout so overlay never gets permanently stuck
 * - Fixed stale iframeDoc caching - always fetches a fresh reference
 * - DEBUG_MODE set to true for testing - set to false before going to production
 * - All hide events log whether they were triggered by detection or timeout
 *
 * Shows a loading overlay when:
 * 1. "Book Now" button is clicked (on main page)
 * 2. Calendar is loading (initial load or month changes)
 * 3. Form submissions with "Book Now" buttons
 *
 * Works alongside bta-boarding-main.js without conflicts
 */

(function() {
  'use strict';

  // ============================================================================
  // DEBUG CONTROL
  // Set to false before going to production
  // ============================================================================
  const DEBUG_MODE = false;

  const debug = {
    log: (...args) => { if (DEBUG_MODE) console.log('[BTA Overlay]', ...args); },
    warn: (...args) => { if (DEBUG_MODE) console.warn('[BTA Overlay]', ...args); },
    error: (...args) => { if (DEBUG_MODE) console.error('[BTA Overlay]', ...args); }
  };

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  const DEFAULT_CONFIG = {
    buttonText: ['book', 'now'],

    overlay: {
      emoji: '🐾',
      title: 'Reviewing Your Bookings',
      message: 'Please wait while we review your reservations...',
      backgroundColor: 'rgba(34, 34, 34, 0.97)',
      zIndex: 999999
    },

    calendarOverlay: {
      emoji: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#BD9B60" width="40" height="40" style="vertical-align: middle; margin-right: 10px; display: inline-block; position: relative; top: 0;"><path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V9h14v10zm0-12H5V5h14v2zM7 11h2v2H7zm4 0h2v2h-4zm4 0h2v2h-2zm-8 4h2v2H7zm4 0h2v2h-4zm4 0h2v2h-2z"/></svg>',
      title: 'Loading Real Time Calendar',
      message: 'Hang tight — we\'re checking what we have available for your pup. This may take a minute.',
      backgroundColor: 'rgba(34, 34, 34, 0.97)',
      zIndex: 999999,
      minDisplayTime: 300
    },

    buttonSelectors: [
      'button',
      'input[type="submit"]',
      'input[type="button"]',
      '[role="button"]'
    ],

    iframe: {
      // How long to search for the BTA iframe before giving up
      // 30 attempts x 500ms = 15 seconds maximum
      maxAttempts: 30,
      retryDelay: 500,
      initialDelay: 0
    },

    calendar: {
      selectors: ['table', '.rdp', '[class*="calendar"]'],
      monthChangeSelectors: [
        'button.rdp-nav_button',
        'button.rdp-nav_button_previous',
        'button.rdp-nav_button_next',
        'button[name="previous-month"]',
        'button[name="next-month"]',
        'button[aria-label*="Go to" i]',
        'button[aria-label*="previous" i]',
        'button[aria-label*="next" i]',
        '.rdp-caption button:not(.rdp-day)',
        '.rdp-nav button',
        'nav button:not(.rdp-day)',
        '.rdp-caption_label + button',
        'button[class*="nav"]:not([class*="day"])',
        'button[class*="arrow"]'
      ],

      // The safety net: overlay will always hide after this many milliseconds
      // regardless of whether calendar detection succeeded
      hardTimeoutMs: 15000
    }
  };

  const CONFIG = window.BTAOverlayConfig
    ? {
        buttonText: window.BTAOverlayConfig.buttonText || DEFAULT_CONFIG.buttonText,
        overlay: { ...DEFAULT_CONFIG.overlay, ...(window.BTAOverlayConfig.overlay || {}) },
        calendarOverlay: { ...DEFAULT_CONFIG.calendarOverlay, ...(window.BTAOverlayConfig.calendarOverlay || {}) },
        buttonSelectors: window.BTAOverlayConfig.buttonSelectors || DEFAULT_CONFIG.buttonSelectors,
        iframe: { ...DEFAULT_CONFIG.iframe, ...(window.BTAOverlayConfig.iframe || {}) },
        calendar: { ...DEFAULT_CONFIG.calendar, ...(window.BTAOverlayConfig.calendar || {}) }
      }
    : DEFAULT_CONFIG;

  // ============================================================================
  // STATE
  // ============================================================================

  const state = {
    overlay: null,
    overlayType: null,
    overlayShownAt: null,
    mainDocListenersInstalled: false,
    iframeListenersInstalled: false,
    calendarObserver: null,   // MutationObserver watching for calendar readiness
    hardTimeoutId: null        // ID of the 15-second safety net timeout
  };

  // ============================================================================
  // OVERLAY IMPLEMENTATION
  // ============================================================================

  function showOverlay(type = 'booking') {
    if (state.overlay) {
      debug.log('⚠️ Overlay already showing, type: ' + state.overlayType);
      return;
    }

    const config = type === 'calendar' ? CONFIG.calendarOverlay : CONFIG.overlay;
    debug.log('✅ Showing ' + type + ' overlay');

    state.overlayType = type;
    state.overlayShownAt = Date.now();

    state.overlay = document.createElement('div');
    state.overlay.id = 'bta-loading-overlay';
    state.overlay.setAttribute('data-type', type);
    state.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${config.backgroundColor};
      z-index: ${config.zIndex};
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      animation: btaOverlayFadeIn 0.3s ease-out;
    `;

    state.overlay.innerHTML = `
      <div style="position: relative; overflow: hidden; text-align: center; padding: 40px 36px 36px; width: calc(100% - 32px); max-width: 480px; box-sizing: border-box; background: #222222; border: 1px solid #BD9B60; border-radius: 14px; box-shadow: 0 16px 48px rgba(0,0,0,0.5);">
        <div style="position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, #7a6236, #BD9B60, #f1dea6, #BD9B60, #7a6236);"></div>
        <div style="display: inline-block; width: 60px; height: 60px; border: 5px solid #464646; border-top: 5px solid #BD9B60;
          border-radius: 50%; animation: btaOverlaySpin 1s linear infinite; margin-bottom: 24px;"></div>
        <div style="font-size: 26px; font-weight: 600; color: #FFFFFF; margin-bottom: 12px; animation: btaOverlayPulse 2s ease-in-out infinite;">
          ${config.emoji} ${config.title}
        </div>
        <div style="font-size: 16px; color: #EAEAEA; line-height: 1.6;">
          ${config.message}
        </div>
      </div>
      <style>
        @keyframes btaOverlaySpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes btaOverlayPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        @keyframes btaOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(state.overlay);
    document.body.style.overflow = 'hidden';

    // Start the hard safety timeout whenever overlay is shown for calendar
    if (type === 'calendar') {
      startHardTimeout();
    }
  }

  function hideOverlay(force = false, reason = 'unknown') {
    if (!state.overlay) {
      return;
    }

    // Enforce minimum display time unless forced
    if (!force && state.overlayType === 'calendar' && state.overlayShownAt) {
      const displayTime = Date.now() - state.overlayShownAt;
      const minTime = CONFIG.calendarOverlay.minDisplayTime;
      if (displayTime < minTime) {
        const remainingTime = minTime - displayTime;
        setTimeout(() => hideOverlay(true, reason), remainingTime);
        return;
      }
    }

    // Always clear safety timeout and observer when hiding
    clearHardTimeout();
    stopCalendarObserver();

    const elapsed = state.overlayShownAt ? Math.round(Date.now() - state.overlayShownAt) : '?';
    debug.log('✅ Overlay hidden — reason: ' + reason + ' — visible for ' + elapsed + 'ms');

    state.overlay.remove();
    state.overlay = null;
    state.overlayType = null;
    state.overlayShownAt = null;
    document.body.style.overflow = '';
  }

  // ============================================================================
  // HARD SAFETY TIMEOUT
  // Guarantees overlay is always removed within 15 seconds no matter what
  // ============================================================================

  function startHardTimeout() {
    clearHardTimeout(); // Clear any existing one first
    debug.log('⏱️ Hard safety timeout started (' + CONFIG.calendar.hardTimeoutMs + 'ms)');
    state.hardTimeoutId = setTimeout(() => {
      if (state.overlay) {
        console.warn('[BTA Overlay] ⚠️ TIMED OUT — calendar was not detected within ' + CONFIG.calendar.hardTimeoutMs + 'ms — hiding overlay');
        hideOverlay(true, 'TIMEOUT after ' + CONFIG.calendar.hardTimeoutMs + 'ms');
      }
    }, CONFIG.calendar.hardTimeoutMs);
  }

  function clearHardTimeout() {
    if (state.hardTimeoutId) {
      clearTimeout(state.hardTimeoutId);
      state.hardTimeoutId = null;
    }
  }

  // ============================================================================
  // MUTATIONOBSERVER — CALENDAR READY DETECTION
  // Watches the BTA iframe for date buttons appearing, which is the definitive
  // signal that the calendar has fully rendered and is ready for interaction.
  // ============================================================================

  function stopCalendarObserver() {
    if (state.calendarObserver) {
      state.calendarObserver.disconnect();
      state.calendarObserver = null;
      debug.log('🔭 Calendar observer disconnected');
    }
  }

  function calendarIsFullyLoaded(doc) {
    // Two conditions must both be true for the calendar to be considered fully loaded:
    // 1. At least one date button exists (calendar is rendering)
    // 2. The submit button exists AND has non-empty text (availability data has finished loading)
    //
    // The submit button text is intentionally not checked — only that it is present and
    // has some text. This means changing the button text in BTA will never break detection.
    const hasDateButtons = doc.querySelectorAll('button.rdp-day').length > 0;
    if (!hasDateButtons) return false;

    const submitButton = doc.querySelector('button.pure-button.fluid');
    const submitHasText = submitButton && (submitButton.textContent || '').trim().length > 0;

    return submitHasText;
  }

  function watchForCalendarReady(doc, context) {
    // context is either 'initial' or 'month-change' for logging clarity
    stopCalendarObserver(); // Always clean up any previous observer first

    debug.log('🔭 Starting MutationObserver — waiting for date buttons + submit button text [' + context + ']');

    // If date buttons already exist right now, no need to observe — hide immediately
    if (calendarIsFullyLoaded(doc)) {
      const elapsed = state.overlayShownAt ? Math.round(Date.now() - state.overlayShownAt) : 0;
      console.log('[BTA Overlay] ✅ CALENDAR FULLY LOADED immediately (dates + submit button ready) [' + context + '] — ' + elapsed + 'ms');
      hideOverlay(false, 'calendar fully loaded immediately [' + context + ']');
      return;
    }

    // Watch for any DOM changes in the iframe — when rdp-day buttons appear, calendar is ready
    state.calendarObserver = new MutationObserver((mutations, observer) => {
      if (calendarIsFullyLoaded(doc)) {
        observer.disconnect();
        state.calendarObserver = null;
        const elapsed = state.overlayShownAt ? Math.round(Date.now() - state.overlayShownAt) : 0;
        console.log('[BTA Overlay] ✅ CALENDAR FULLY LOADED via MutationObserver (dates + submit button ready) [' + context + '] — ' + elapsed + 'ms after overlay shown');
        hideOverlay(false, 'calendar fully loaded via observer [' + context + ']');
      }
    });

    // Observe the entire iframe document for any subtree changes
    const target = doc.body || doc.documentElement;
    state.calendarObserver.observe(target, {
      childList: true,
      subtree: true
    });

    debug.log('🔭 Observer active — waiting for button.rdp-day elements');
  }

  // ============================================================================
  // BOOK NOW BUTTON DETECTION
  // ============================================================================

  function isBookNowButton(element) {
    if (!element) return false;
    const text = (element.textContent || element.value || '').toLowerCase();
    return CONFIG.buttonText.every(word => text.includes(word));
  }

  function handleBookNowClick(e, source = 'main') {
    let button = null;
    for (const selector of CONFIG.buttonSelectors) {
      button = e.target.closest(selector);
      if (button) break;
    }
    if (!button) return;
    if (isBookNowButton(button)) {
      debug.log('✅ Book Now button clicked in ' + source);
      showOverlay('booking');
    }
  }

  function handleFormSubmit(e, source = 'main') {
    const form = e.target;
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitButton && isBookNowButton(submitButton)) {
      debug.log('✅ Book Now form submitted in ' + source);
      showOverlay('booking');
    }
  }

  // ============================================================================
  // CALENDAR NAVIGATION DETECTION
  // ============================================================================

  function isMonthNavigationButton(element) {
    if (!element || element.tagName !== 'BUTTON') return false;
    if (element.classList.contains('rdp-day')) return false;

    for (const selector of CONFIG.calendar.monthChangeSelectors) {
      try {
        if (element.matches(selector)) return true;
      } catch (e) { /* invalid selector, skip */ }
    }

    const parentNav = element.closest('nav, .rdp-nav, .rdp-caption, [class*="nav"]');
    if (parentNav && !element.classList.contains('rdp-day')) return true;

    const ariaLabel = (element.getAttribute('aria-label') || '').toLowerCase();
    if (ariaLabel.includes('previous month') || ariaLabel.includes('next month') ||
        ariaLabel.includes('previous') || ariaLabel.includes('next') ||
        ariaLabel.includes('go to')) return true;

    if (element.closest('.rdp-caption_label')) return true;

    const classes = element.className.toLowerCase();
    if ((classes.includes('nav') || classes.includes('arrow') ||
         classes.includes('previous') || classes.includes('next')) &&
        !classes.includes('day')) return true;

    return false;
  }

  function handleClick(e, source = 'iframe') {
    const button = e.target.closest('button');
    if (!button) return;

    if (button.classList.contains('rdp-day')) return; // Date buttons handled by boarding bundle

    if (isMonthNavigationButton(button)) {
      debug.log('🗓️ Month navigation clicked — showing calendar overlay');
      // Get a fresh doc reference for the observer
      const doc = getFreshIframeDoc();
      if (doc) {
        showOverlay('calendar');
        // Brief delay to let BTA start re-rendering before we start watching
        setTimeout(() => watchForCalendarReady(doc, 'month-change'), 150);
      }
      return;
    }

    if (isBookNowButton(button)) {
      debug.log('✅ Book Now button clicked in ' + source);
      showOverlay('booking');
    }
  }

  // ============================================================================
  // IFRAME ACCESS — never caches, always returns a fresh reference
  // ============================================================================

  function getFreshIframeDoc() {
    const selectors = [
      'iframe[title="widget_xcomponent"]',
      '#bta-widget-container iframe',
      'iframe[id*="bta-product-widget"]',
      'iframe[src*="book.app"]',
      'iframe[src*="bookthatapp"]'
    ];

    for (const selector of selectors) {
      const candidates = document.querySelectorAll(selector);
      for (const candidate of candidates) {
        if (candidate.offsetWidth > 0 && candidate.offsetHeight > 0) {
          try {
            const doc = candidate.contentDocument || candidate.contentWindow?.document;
            if (doc && doc.body) {
              debug.log('✓ Fresh iframe doc found via: ' + selector);
              return doc;
            }
          } catch (e) { /* cross-origin, skip */ }
        }
      }
    }

    debug.log('❌ No accessible iframe found');
    return null;
  }

  function findCalendar(doc) {
    for (const selector of CONFIG.calendar.selectors) {
      const el = doc.querySelector(selector);
      if (el) {
        debug.log('✓ Calendar element found via: ' + selector);
        return el;
      }
    }
    return null;
  }

  // ============================================================================
  // LISTENER INSTALLATION
  // ============================================================================

  function installMainDocListeners() {
    if (state.mainDocListenersInstalled) return;
    document.addEventListener('click', (e) => handleBookNowClick(e, 'main'), true);
    document.addEventListener('submit', (e) => handleFormSubmit(e, 'main'), true);
    state.mainDocListenersInstalled = true;
    debug.log('✅ Main document listeners installed');
  }

  function installIframeListeners(doc) {
    if (state.iframeListenersInstalled) return;
    doc.addEventListener('click', (e) => handleClick(e, 'iframe'), true);
    doc.addEventListener('submit', (e) => handleFormSubmit(e, 'iframe'), true);
    state.iframeListenersInstalled = true;
    debug.log('✅ Iframe listeners installed');
  }

  // ============================================================================
  // OTHER IFRAMES
  // ============================================================================

  function attachToIframe(iframe) {
    if (iframe.btaOverlayAttached) return false;
    try {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return false;
      iframeDoc.addEventListener('click', (e) => handleClick(e, 'other-iframe'), true);
      iframeDoc.addEventListener('submit', (e) => handleFormSubmit(e, 'other-iframe'), true);
      iframe.btaOverlayAttached = true;
      return true;
    } catch (e) { return false; }
  }

  function checkForOtherIframes() {
    document.querySelectorAll('iframe').forEach(iframe => {
      if (!iframe.btaOverlayAttached) attachToIframe(iframe);
    });
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  function init() {
    debug.log('═══════════════════════════════════════════');
    debug.log('BTA Overlay v2.0 — INITIALIZATION STARTED');
    debug.log('Hard timeout: ' + CONFIG.calendar.hardTimeoutMs + 'ms');
    debug.log('Detection method: MutationObserver on button.rdp-day');
    debug.log('═══════════════════════════════════════════');

    // Show overlay immediately — hard timeout starts inside showOverlay
    showOverlay('calendar');
    installMainDocListeners();

    let attempts = 0;

    const tryInit = () => {
      attempts++;
      debug.log('Init attempt ' + attempts + '/' + CONFIG.iframe.maxAttempts);

      const doc = getFreshIframeDoc();

      if (!doc) {
        if (attempts < CONFIG.iframe.maxAttempts) {
          setTimeout(tryInit, CONFIG.iframe.retryDelay);
        } else {
          console.warn('[BTA Overlay] ⚠️ TIMED OUT — BTA iframe not found after ' + attempts + ' attempts — hiding overlay');
          hideOverlay(true, 'TIMEOUT — iframe not found after ' + attempts + ' attempts');
        }
        return;
      }

      const calendar = findCalendar(doc);
      if (!calendar) {
        if (attempts < CONFIG.iframe.maxAttempts) {
          setTimeout(tryInit, CONFIG.iframe.retryDelay);
        } else {
          console.warn('[BTA Overlay] ⚠️ TIMED OUT — calendar element not found after ' + attempts + ' attempts — hiding overlay');
          hideOverlay(true, 'TIMEOUT — calendar element not found after ' + attempts + ' attempts');
        }
        return;
      }

      debug.log('✓ Iframe and calendar found on attempt ' + attempts + ' — starting observer');
      installIframeListeners(doc);

      // Hand off to MutationObserver — it will hide the overlay when ready
      watchForCalendarReady(doc, 'initial');
    };

    // Scan for other iframes periodically
    [500, 1000, 2000].forEach(delay => setTimeout(checkForOtherIframes, delay));
    const otherIframeObserver = new MutationObserver(checkForOtherIframes);
    otherIframeObserver.observe(document.body, { childList: true, subtree: true });

    // Start BTA iframe detection
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(tryInit, CONFIG.iframe.initialDelay);
      });
    } else {
      setTimeout(tryInit, CONFIG.iframe.initialDelay);
    }
  }

  init();

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  window.BTALoadingOverlay = {
    show: showOverlay,
    hide: hideOverlay,
    showCalendar: () => showOverlay('calendar'),
    showBooking: () => showOverlay('booking'),
    config: CONFIG,
    state: state,
    checkStatus: function() {
      const status = {
        mainDocListeners: state.mainDocListenersInstalled,
        iframeListeners: state.iframeListenersInstalled,
        overlayShowing: !!state.overlay,
        overlayType: state.overlayType,
        observerActive: !!state.calendarObserver,
        hardTimeoutActive: !!state.hardTimeoutId,
        overlayAgeMs: state.overlayShownAt ? Math.round(Date.now() - state.overlayShownAt) : null
      };
      console.log('[BTA Overlay] Status:', status);
      return status;
    },

    // Test the booking overlay — call BTALoadingOverlay.hide(true, "manual") to dismiss
    testOverlay: function() {
      console.log('[BTA Overlay] 🧪 Testing booking overlay — call BTALoadingOverlay.hide(true, "manual") to remove it');
      showOverlay('booking');
    },

    // Test the calendar overlay — auto-hides after 3 seconds
    testCalendarOverlay: function() {
      console.log('[BTA Overlay] 🧪 Testing calendar overlay — will auto-hide in 3 seconds');
      showOverlay('calendar');
      setTimeout(() => hideOverlay(true, 'test'), 3000);
    }
  };

  debug.log('📦 Public API ready: window.BTALoadingOverlay');
  debug.log('🔍 Check status: BTALoadingOverlay.checkStatus()');
  debug.log('❌ Force hide: BTALoadingOverlay.hide(true, "manual")');

})();